import { useNavigate } from "react-router-dom";
import { useLang } from "@/App";
import { UI, tr, COUNTRY_NAMES } from "@/i18n";
import { ArrowRight, ScanLine, BarChart3, Compass } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1543783207-ec64e4d95325?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const STEPS = [
  { icon: ScanLine, t: "step1_t", d: "step1_d" },
  { icon: BarChart3, t: "step2_t", d: "step2_d" },
  { icon: Compass, t: "step3_t", d: "step3_d" },
];

const FLAGS = [
  ["ES", "🇪🇸"], ["PT", "🇵🇹"], ["GR", "🇬🇷"], ["IT", "🇮🇹"], ["DE", "🇩🇪"], ["FR", "🇫🇷"],
  ["AT", "🇦🇹"], ["MT", "🇲🇹"], ["GB", "🇬🇧"], ["TR", "🇹🇷"], ["US", "🇺🇸"], ["AE", "🇦🇪"], ["AR", "🇦🇷"],
];

const Landing = () => {
  const { lang } = useLang();
  const navigate = useNavigate();

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 lg:gap-6 items-center pt-14 pb-16 lg:pt-24 lg:pb-24">
          <div className="om-fade-up">
            <p className="overline text-gold mb-5">{tr(lang, UI.tagline)}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-navy-deep">
              {tr(lang, UI.hero_title)}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              {tr(lang, UI.hero_sub)}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                data-testid="hero-start-btn"
                onClick={() => navigate("/assessment")}
                className="group inline-flex items-center gap-2 bg-navy text-white px-7 py-4 rounded-sm font-semibold hover:bg-navy-deep transition-colors duration-300"
              >
                {tr(lang, UI.start)}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <span className="text-sm text-muted-foreground">{tr(lang, UI.hero_time)}</span>
            </div>
          </div>

          <div className="om-fade-up relative" style={{ animationDelay: "0.15s" }}>
            <div className="relative aspect-[4/5] sm:aspect-[3/4] rounded-sm overflow-hidden om-grain shadow-2xl">
              <img src={HERO} alt="Coastal European city at golden hour" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 backdrop-blur-md bg-white/85 rounded-sm p-4 flex items-center justify-between">
                <div>
                  <p className="text-[11px] overline text-muted-foreground">{tr(lang, UI.match)}</p>
                  <p className="font-display text-2xl text-navy">🇪🇸 {tr(lang, COUNTRY_NAMES.ES)} — 92%</p>
                </div>
                <div className="text-right text-[11px] text-muted-foreground leading-tight">
                  🇵🇹 84%<br />🇬🇷 79%
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="bg-navy-deep text-white relative om-grain">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <div className="grid md:grid-cols-3 gap-8 lg:gap-14">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="om-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="flex items-center gap-4 mb-4">
                    <span className="font-display text-3xl text-gold">0{i + 1}</span>
                    <span className="h-px flex-1 bg-white/15" />
                    <Icon className="w-5 h-5 text-white/60" />
                  </div>
                  <h3 className="font-display text-2xl mb-2">{tr(lang, UI[s.t])}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{tr(lang, UI[s.d])}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-20 text-center">
        <p className="overline text-gold mb-3">{tr(lang, UI.nav_destinations)}</p>
        <h2 className="font-display text-3xl sm:text-4xl text-navy-deep mb-8">{tr(lang, UI.dest_heading)}</h2>
        <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
          {FLAGS.map(([code, flag]) => (
            <span key={code} className="inline-flex items-center gap-2 bg-white border border-border rounded-full px-4 py-2 text-sm font-medium">
              <span className="text-lg">{flag}</span>{tr(lang, COUNTRY_NAMES[code])}
            </span>
          ))}
        </div>
        <button
          data-testid="destinations-start-btn"
          onClick={() => navigate("/assessment")}
          className="mt-10 inline-flex items-center gap-2 border border-navy text-navy px-7 py-3.5 rounded-sm font-semibold hover:bg-navy hover:text-white transition-colors duration-300"
        >
          {tr(lang, UI.start)} <ArrowRight className="w-4 h-4" />
        </button>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
          <p className="text-xs text-muted-foreground leading-relaxed max-w-4xl">{tr(lang, UI.disclaimer)}</p>
          <p className="mt-4 text-xs text-muted-foreground font-display">ONE Match · <span className="text-gold">{tr(lang, UI.by)}</span></p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
